import {Bindings} from "data/constants/bindings";
import {ModalType} from "data/enums";
import type {ILocalizationStore} from "data/stores/localization/localization.store";
import type {IModalsStore} from "data/stores/modals/modals.store";
import type {IPlayer, IPlayersStore} from "data/stores/players/players.store";
import {ViewController} from "data/types/structure";
import {inject, injectable} from "inversify";
import {action, makeAutoObservable, observable} from "mobx";
import {SyntheticEvent} from "react";

export interface IModalPlayerProfileController extends ViewController {
	get i18n(): ILocalizationStore;
	get isOpen(): boolean;
	get player(): IPlayer | undefined;
	get selectedTab(): string;
	get tabs(): {label: string; value: "overview" | "sessions"}[];

	onTabChange: (_event: SyntheticEvent, value: string) => void;
	close: () => void;
	resetModal: () => void;
}

@injectable()
export class ModalPlayerProfileController implements IModalPlayerProfileController {
	@observable _selectedTab: string = this.tabs[0].value;

	constructor(
		@inject(Bindings.LocalizationStore) public readonly i18n: ILocalizationStore,
		@inject(Bindings.ModalsStore) private readonly _modalsStore: IModalsStore,
		@inject(Bindings.PlayersStore) private _playersStore: IPlayersStore
	) {
		makeAutoObservable(this);
	}

	get isOpen(): boolean {
		return this._modalsStore.modal === ModalType.PLAYER_PROFILE;
	}

	get player() {
		if (!this.modalData?.playerId) {
			return;
		}

		return this._playersStore.getPlayerById(this.modalData?.playerId);
	}

	protected get modalData() {
		return this._modalsStore.modalContent;
	}

	get selectedTab() {
		return this._selectedTab;
	}

	get tabs() {
		return [
			{
				label: this.i18n.t("player_profile.tabs.overview", "Overview"),
				value: "overview" as const,
			},
			{
				label: this.i18n.t("player_profile.tabs.sessions", "Sessions"),
				value: "sessions" as const,
			},
		];
	}

	@action onTabChange = (_event: SyntheticEvent, value: string) => {
		this._selectedTab = value;
	};

	@action resetModal = () => {
		this._selectedTab = this.tabs[0].value;
	};

	close = () => {
		return this._modalsStore.hideModal();
	};
}
